import {EEToolAdapterContextType} from "../../types/EEToolAdapterContextType";
import {SyncRequestClient} from 'ts-sync-request';
import {API_URL} from "../../../../../config/constants";
import {ProductImage} from "../../types/ProductImage";

export interface IdDescriptionPair {
    Id: number;
    Description: string;
}

export interface QuoteItem {
    ProductId: number;
    Quantity: number;
}

export interface ProductIdOnly {
    ProductID: number;
}

export interface JointProduct {
    ProductId: number;
    JointProductId: number;
}

export interface ItemSelectionList {
    SelectedItemIndex: number;
    ItemList: IdDescriptionPair[];
}

export class ApiService {

    static cache: any = {};
    static dealerId: number;
    static zip: string;

    static initialize(dealerId: number, zip: string) {
        ApiService.dealerId = dealerId;
        ApiService.zip = zip;
    }

    static getFromCache(key: string) {
        if (this.cache[key])
            return this.cache[key];
        return null;
    }

    static getCacheKey(functionName: string, ...restArguments: string[]) {
        var result = functionName + "-" + ApiService.zip + "-" + ApiService.dealerId;
        if (restArguments.length > 0) {
            result = result + "-" + restArguments.join("-");
        }
        return result;
    }

    static getImageFromProductID(productID: number, adapter: EEToolAdapterContextType) {
        let cacheKey = this.getCacheKey("GetBase64ImgString", productID.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem) {
            let imgTag = document.createElement("img");
            imgTag.setAttribute("src", cachedItem.base64String);
            imgTag.setAttribute("alt", cachedItem.fileName);
            imgTag.setAttribute("width", cachedItem.width);
            imgTag.setAttribute("height", cachedItem.height);
            return imgTag;
        }

        try {
            cachedItem = new SyncRequestClient()
                .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
                .get<ProductImage>(API_URL + 'ee-tool/image?productID=' + productID);

            if (cachedItem) {
                ApiService.cache[cacheKey] = cachedItem;

                let imgTag = document.createElement("img");
                imgTag.setAttribute("src", cachedItem.base64String);
                imgTag.setAttribute("alt", cachedItem.fileName);
                imgTag.setAttribute("width", cachedItem.width);
                imgTag.setAttribute("height", cachedItem.height);
                return imgTag;
            }
        } catch (error) {
            console.log(error);
        }

        return undefined;
    }

    static getSinkListFromServer(productQuoteGroupId: number) {
        if (productQuoteGroupId == null) {
            return [];
        }

        let cacheKey = this.getCacheKey("FetchSinkList", productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let sinkList = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<IdDescriptionPair[]>(API_URL + 'ee-tool/sinks?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId + '&zipCode=' + ApiService.zip);
        ApiService.cache[cacheKey] = sinkList;
        return sinkList;
    }

    static getCapListFromServer(productQuoteGroupId: number): IdDescriptionPair[] {
        if (productQuoteGroupId == null) {
            return [];
        }
        let cacheKey = this.getCacheKey("FetchCapList", productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let capList = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<IdDescriptionPair[]>(API_URL + 'ee-tool/caps?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId + '&zipCode=' + ApiService.zip);

        ApiService.cache[cacheKey] = capList;
        return capList;
    }

    static getDiagonalOptionListFromServer(productQuoteGroupId: number): IdDescriptionPair[] {
        if (productQuoteGroupId == null) {
            return [];
        }

        let cacheKey = this.getCacheKey("FetchDiagonalOptionList", ApiService.zip, productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let options = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<IdDescriptionPair[]>(API_URL + 'ee-tool/diagonal-options?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId + '&zipCode=' + ApiService.zip);

        ApiService.cache[cacheKey] = options;
        return options;
    }

    static getSplashListFromServer(productQuoteGroupId: number): IdDescriptionPair[] {
        if (productQuoteGroupId == null) {
            return [];
        }

        let cacheKey = this.getCacheKey("FetchSplashList", productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let splashes = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<IdDescriptionPair[]>(API_URL + 'ee-tool/splashes?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId + '&zipCode=' + ApiService.zip);

        ApiService.cache[cacheKey] = splashes;
        return splashes;
    }

    static getJointProductIDs(): any {

        let cacheKey = this.getCacheKey("GetJointProductIDs");
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        cachedItem = {};
        let result = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<JointProduct[]>(API_URL + 'ee-tool/joint-products?dealerID=' + ApiService.dealerId);

        for (let item of result) {
            cachedItem[item.ProductId] = item.JointProductId;
        }

        ApiService.cache[cacheKey] = cachedItem;

        return result;
    }

    static getSinkInstallProductID(productQuoteGroupId: number | null, sinkId: number, ironType: number | null): number {
        let cacheKey = this.getCacheKey("GetProductIDForSinkLabor", (productQuoteGroupId ?? 0).toString(), sinkId.toString(), ironType?.toString() ?? 'X');
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let sinkInstallProductId = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<number>(API_URL + 'ee-tool/sink-labor-product?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId + '&ironType=' + (ironType?.toString() ?? '0'));

        ApiService.cache[cacheKey] = sinkInstallProductId;

        return sinkInstallProductId;
    }

    static getRadiusProductIDArr(productQuoteGroupId: number): number[] {
        let cacheKey = this.getCacheKey("GetProductIDArrForRadius", productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let d = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<number[]>(API_URL + 'ee-tool/radius-products?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId + '&zipCode=' + ApiService.zip);

        var radiusProductIDArr: number[] = [];
        radiusProductIDArr.push(-1);
        if (d) {
            radiusProductIDArr = radiusProductIDArr.concat(d);
            ApiService.cache[cacheKey] = radiusProductIDArr;
        }

        return radiusProductIDArr;
    }

    static getDiagonalProductID(productQuoteGroupId: number): number {

        let cacheKey = this.getCacheKey("GetProductIDForDiagonal", productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let diagonalProduct = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<ProductIdOnly>(API_URL + 'ee-tool/diagonal-product?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId);
        ApiService.cache[cacheKey] = diagonalProduct.ProductID;

        return diagonalProduct.ProductID;
    }

    static getClippedCornerProductID(productQuoteGroupId: number): number {

        let cacheKey = this.getCacheKey("getClippedCornerProductID", productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let product = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<ProductIdOnly>(API_URL + 'ee-tool/clipped-corner-product?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId);
        ApiService.cache[cacheKey] = product.ProductID;

        return product.ProductID;
    }

    static getLaminateFullRadiusProductID(productQuoteGroupId: number): number {

        let cacheKey = this.getCacheKey("getLaminateFullRadiusProductID", ApiService.zip, productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }

        let product = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<ProductIdOnly>(API_URL + 'ee-tool/laminate-full-radius-product?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId);
        ApiService.cache[cacheKey] = product.ProductID;

        return product.ProductID;
    }

    static getCutoutProductID(productQuoteGroupId: number) {
        let cacheKey = this.getCacheKey("GetProductIDForCutout", productQuoteGroupId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }
        let cutoutProductId = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<number>(API_URL + 'ee-tool/cutout-product?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productQuoteGroupId);
        ApiService.cache[cacheKey] = cutoutProductId;

        return cutoutProductId;
    }

    static getSplashUpgradeList(productId: number): IdDescriptionPair[] {
        let cacheKey = this.getCacheKey("GetSplashUpgradeList", productId.toString());
        let cachedItem = this.getFromCache(cacheKey);
        if (cachedItem !== null) {
            return cachedItem;
        }
        let result = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<IdDescriptionPair[]>(API_URL + 'ee-tool/splash-upgrades?dealerID=' + ApiService.dealerId + '&prodQteGrpID=' + productId + '&zipCode=' + ApiService.zip);
        ApiService.cache[cacheKey] = result;

        return result;
    }

    static async saveTempQuote(quoteId: number, drawingData: string, quoteItemArr: QuoteItem[]) {
        let dataTransferObject = {
            quoteID: quoteId,
            dealerID: ApiService.dealerId,
            drawingData: JSON.stringify(drawingData),
            quoteItemArr: quoteItemArr
        };
        //TODO:
        //await apiClient.post("Pages/Store/QuotingTool/DrawingCanvasService.asmx/SaveTempQuote", dataTransferObject);
    }

    static loadQuoteDrawingData(quoteId: string, useTemp: boolean): JQueryPromise<string> {
        var def = $.Deferred<any>();

        $.ajax({
            type: "POST",
            url: "Pages/Store/QuotingTool/DrawingCanvasService.asmx/LoadQuoteDrawingData",
            data: '{quoteID: "' + quoteId + '", useTemp: ' + useTemp + '}',
            contentType: "application/json; charset=utf-8",
            dataType: "json",
            success(jsonData) {
                def.resolve(jsonData.d);
            },
            error(msg) {
                if (typeof msg !== "undefined" && typeof msg.responseText !== "undefined") {
                    alert(msg.responseText);
                }
                def.reject();
            }
        });

        return def.promise();
    }

    static getWoodProductIDs(): number[] {
        let cacheKey = this.getCacheKey("getWoodProductIDs");
        let cachedItems = this.getFromCache(cacheKey);
        if (cachedItems !== null) {
            return cachedItems;
        }
        let d: ProductIdOnly[] = new SyncRequestClient()
            .addHeader("Authorization", "Bearer " + localStorage.getItem('access_token'))
            .get<ProductIdOnly[]>(API_URL + 'ee-tool/wood-products');
        cachedItems = [];
        if (d) {
            for (var i = 0; i < d.length; i++) {
                cachedItems.push(d[i].ProductID);
            }
        }
        ApiService.cache[cacheKey] = cachedItems;
        return cachedItems;
    }
}
